import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import { getThemeClasses } from '../../utils/theme';
import ArgusLogo from '../common/ArgusLogo';
import Footer from '../common/Footer';

const LoginScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { isDarkMode } = useTheme();
  const theme = getThemeClasses(isDarkMode);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      await login();
      // keycloak.login() redirects, so we don't need to navigate
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <ArgusLogo size="xl" variant="light" />
            </div>
            <p className="text-slate-400 mt-4">Medical Benefits Audit Platform</p>
          </div>

          {/* Login Card */}
          <div className={`${theme.bgSecondary} rounded-2xl shadow-2xl p-8`}>
            <div className="mb-6 text-center">
              <h2 className={`text-xl font-semibold ${theme.textPrimary}`}>Welcome</h2>
              <p className={`${theme.textTertiary} text-sm mt-1`}>Sign in to access your dashboard</p>
            </div>

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 focus:ring-4 focus:ring-blue-200 transition disabled:opacity-70 flex items-center justify-center mb-4"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Connecting to Identity Provider...
                </>
              ) : (
                'Sign In with SSO'
              )}
            </button>

            {/* PIV/CAC Card Option */}
            <div className={`pt-4 border-t ${theme.border}`}>
              <button
                onClick={handleLogin}
                className={`w-full flex items-center justify-center gap-2 py-3 border ${theme.border} rounded-lg ${theme.textSecondary} ${theme.bgHover} transition`}
              >
                <Lock className="w-4 h-4" />
                Sign in with PIV/CAC Card
              </button>
            </div>
          </div>

          {/* Compliance badges */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm">
              Authorized use only. All activities are monitored and logged.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-400">
              <span>FedRAMP Authorized</span>
              <span>•</span>
              <span>HIPAA Compliant</span>
              <span>•</span>
              <span>SOC 2 Type II</span>
            </div>
          </div>
        </div>
      </div>

      <Footer variant="light" />
    </div>
  );
};

export default LoginScreen;

